import { fileChecksum } from "./fileChecksum";
import sh from "./sh";

// Requests a pre-signed S3 PUT url for a job offer media file (image or video).
// Unlike uploadProfilePicture, the real file content type is forwarded so the
// backend can accept both images and videos (folder: job_offer_medias).
const createPresignedUrl = async (file, byteSize, checksum) => {
  const options = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      file: {
        filename: file.name,
        byte_size: byteSize,
        checksum: checksum,
        content_type: file.type,
        metadata: {
          folder: "job_offer_medias",
        },
      },
    }),
  };
  const res = await fetch("/presigned_url", options);
  if (res.status !== 200) return res;
  return await res.json();
};

// PUTs the file to S3 using XHR so upload progress can be reported.
// Resolves on 2xx, rejects otherwise.
const putToS3WithProgress = (url, headers, body, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([key, value]) =>
      xhr.setRequestHeader(key, value)
    );
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve(xhr) : reject(xhr);
    xhr.onerror = () => reject(xhr);
    xhr.send(body);
  });

// Phase 1: uploads the file bytes to S3 and returns the blob's signed id.
// This is the slow part and needs NO job offer id, so it can run in the
// background (e.g. while the offer is still being created). onProgress
// (optional) receives an integer 0-100 during the S3 upload.
export const uploadJobOfferMediaToS3 = async (file, onProgress) => {
  const checksum = await fileChecksum(file);
  const presignedFileParams = await createPresignedUrl(
    file,
    file.size,
    checksum
  );

  await putToS3WithProgress(
    presignedFileParams.direct_upload.url,
    presignedFileParams.direct_upload.headers,
    file,
    onProgress
  );

  return presignedFileParams.blob_signed_id;
};

// Phase 2: attaches an already-uploaded blob to a persisted job offer.
// Cheap JSON call — needs the job offer id. attributeName is
// "job_offer_images" or "job_offer_videos".
export const attachJobOfferMedia = async (
  blobSignedId,
  jobOfferId,
  attributeName,
  authenticityToken
) => {
  const params = {
    authenticity_token: authenticityToken,
    job_offer: {
      record_id: jobOfferId,
      attribute_name: attributeName,
      picture: blobSignedId,
    },
  };
  const res = await sh.put("/job_offer/upload_media", params);
  if (res.status !== 200) return res;
  return res.data;
};

// Uploads a single media file to S3 then confirms it with the backend.
// attributeName is "job_offer_images" or "job_offer_videos".
// onProgress (optional) receives an integer 0-100 during the S3 upload.
export const uploadJobOfferMedia = async (
  file,
  jobOfferId,
  attributeName,
  authenticityToken,
  onProgress
) => {
  const blobSignedId = await uploadJobOfferMediaToS3(file, onProgress);
  return attachJobOfferMedia(
    blobSignedId,
    jobOfferId,
    attributeName,
    authenticityToken
  );
};
